// History V3.15
// used to load pages through ajax
// stores loaded pages in sessionStorage
// remembers what pages are present in which elements

// init: history = new History({ cookiesPrefix: 'myproject_' });
// then call: history.init();

// requires these globals to be set before each page switch (e.g. in #vars script):
// $pageType: string — current page type, set on body as data-pagetype
// $historySelector: string — selector of the element to swap content into (e.g. '.main')
// $wcPages: array (optional) — list of WooCommerce URL fragments that should use default navigation

// required properties:
// cookiesPrefix: prefix used for localStorage keys (e.g. 'myproject_')

// possible properties:
// useCache = false: cache full page HTML in sessionStorage for instant back/forward
// projectTitle: default document title used for history.pushState, defaults to current document.title
// viewTransitions = false: use the View Transitions API when swapping page content
// excludedHrefParts = []: additional href fragments that should always use default navigation
// popstateCaptureUrls = []: urls that should be intercepted on popstate (browser buttons) instead of ajax loading
// capturePopstate = url => {}: callback called with the new popstate url when it is intercepted. Only happens when using browser buttons.
// setScrollTop = () => {}: function called to set scroll position (e.g. animateIt.setScrollPosHard)
// samePage = () => {}: called when a link to the current page (same URL) is clicked
// animateBefore = setDoneAnimating => setDoneAnimating(): called to run an exit animation before swapping content, must call the passed callback when done
// beforeSwitch = () => {}: called just before the DOM is swapped
// newContent = () => {}: called after new content is placed in the DOM and scripts are executed
// afterSetScroll = () => {}: called after setting the scroll position, so scroll dependant position are correct
// loadEventDelay = 400: ms after content swap before a synthetic load event is dispatched (for fader etc.)
// resetLineDelay = 800: delay in ms before resetting the loader line back to default after load

// methods:
// history.init(): attach all click/submit/popstate/scroll listeners — call once on page load
// history.destroy(): remove all listeners
// history.navigateTo(url): programmatically navigate to a url
// history.setPageTypeNext(type): set the next page type ahead of a link click
// history.initLoadPage(): manually trigger loading of window.location.href
// history.checkWoocommerce(url): returns true if url should use default navigation
// history.addExcludedHrefParts(parts): append href fragments to default-navigation exclusion list
// history.setExcludedHrefParts(parts): replace href fragment exclusions

// changelog:
// 3.0 initial version
// 3.1 basic fixes
// 3.2 remove redundant history prefixes from class state/api, add loader line naming cleanup
// 3.3 make init the only public method, keep the rest private
// 3.4 restore setPageTypeNext and initLoadPage as public project hooks
// 3.5 expose historyCheckWoocommerce as public method (was unreachable as module-level function)
// 3.6 add destroy(), extract anonymous listeners into private methods so they can be removed
// 3.7 add navigateTo(url) for programmatic navigation
// 3.8 remove checkWoocommerce wrapper, changed order and timeouts
// 3.9 split callbacks to newContent (after DOM swap) and afterSetScroll (after setting scroll) for more granular control, like in Page.setPage and Page.setPageAfterSetScroll
// 3.10 add option to capture popstate on specific urls and do other things than loading a page
// 3.11 #setHistory in view transition, so changes animate
// 3.12 remove adding 'popover' to url, cleanup same page check, add checkSamePage method
// 3.13 make link exclusion fragments configurable via constructor + public methods
// 3.14 apply setSavedScroll during init so refresh restores saved scroll position
// 3.15 skip setting scroll to top if the content was already in the dom

const isExternalLink = url => {
  const tmp = document.createElement('a');
  tmp.href = url;
  return tmp.host !== window.location.host;
};

const executeScript = (html, el) => {
  if (!el) return;

  const scriptEl = document.createElement('script');
  scriptEl.innerHTML = html;
  el.innerHTML = '';
  el.appendChild(scriptEl);
};

export default class History {
  #boundSaveScroll;
  #boundBeforeUnload;
  #boundPopstate;
  #boundSubmit;
  #boundClick;

  constructor({
    useCache = false,
    projectTitle,
    viewTransitions = false,
    excludedHrefParts = [],
    popstateCaptureUrls = [],
    capturePopstate = () => {},
    cookiesPrefix,
    setScrollTop = () => {},
    samePage = () => {},
    animateBefore = setDoneAnimating => setDoneAnimating(),
    beforeSwitch = () => {},
    newContent = () => {},
    afterSetScroll = () => {},
    loadEventDelay = 400,
    resetLineDelay = 800,
  } = {}) {
    if (typeof cookiesPrefix === 'undefined') {
      throw new Error('History: cookiesPrefix is a required parameter.');
    }

    // config variables
    this.useCache = useCache;
    this.projectTitle = projectTitle || document.title;
    this.viewTransitions = viewTransitions;
    this.excludedHrefParts = [];
    this.addExcludedHrefParts(excludedHrefParts);
    this.popstateCaptureUrls = Array.isArray(popstateCaptureUrls) ? popstateCaptureUrls : [];
    this.capturePopstate = typeof capturePopstate === 'function' ? capturePopstate : () => {};
    this.loadEventDelay = loadEventDelay;
    this.resetLineDelay = resetLineDelay;
    this.cookiesPrefix = cookiesPrefix;

    this.setScrollTop = setScrollTop;
    this.samePage = samePage;
    this.animateBefore = animateBefore;
    this.beforeSwitch = beforeSwitch;
    this.newContent = newContent;
    this.afterSetScroll = afterSetScroll;

    // code variables
    this.currentPages = [];

    this.isSwitching = false;
    this.switchAgain = false;
    this.canSaveScroll = true;

    this.pageElement = '';

    this.doneAnimating = false;
    this.doneScrolling = false;
    this.doneLoading = false;
    this.skipScrollReset = false;

    this.hasSetHtml = false;
    this.pageTypeNext = '';
    this.pageTypePrev = '';
    this.selectorPrev = '';
    this.formData = undefined;

    this.cache = JSON.parse(sessionStorage.getItem('historyCache'));
    if (this.cache === null) this.cache = [];
    this.url = window.location.href;
    this.hasLoaded = false;

    this.usedBrowser = false;
    this.currentCacheIndex = 0;

    this.loaderEl = document.querySelector('.history-loader');

    this.#boundSaveScroll = this.#saveScroll.bind(this);
    this.#boundBeforeUnload = this.#handleBeforeUnload.bind(this);
    this.#boundPopstate = this.#handlePopstate.bind(this);
    this.#boundSubmit = this.#handleSubmit.bind(this);
    this.#boundClick = this.#handleClick.bind(this);
  }

  init = () => {
    document.addEventListener('scroll', this.#boundSaveScroll);
    window.addEventListener('beforeunload', this.#boundBeforeUnload);

    this.#setHistory();

    if (history.scrollRestoration) history.scrollRestoration = 'manual';

    window.addEventListener('popstate', this.#boundPopstate, false);

    document.body.addEventListener('submit', this.#boundSubmit);

    document.body.addEventListener('click', this.#boundClick);

    requestAnimationFrame(this.setSavedScroll); // after resize
  }

  destroy = () => {
    document.removeEventListener('scroll', this.#boundSaveScroll);
    window.removeEventListener('beforeunload', this.#boundBeforeUnload);
    window.removeEventListener('popstate', this.#boundPopstate);
    document.body.removeEventListener('submit', this.#boundSubmit);
    document.body.removeEventListener('click', this.#boundClick);
  }

  #handleBeforeUnload() {
    sessionStorage.setItem('historyCache', JSON.stringify(this.cache));
  }

  #handlePopstate() {
    const popstateUrl = window.location.href;
    // console.log('popstate', {url: popstateUrl, state: history.state});

    if (this.#shouldCapturePopstate(popstateUrl)) {
      this.capturePopstate(popstateUrl);
      return;
    }

    if (!this.#checkDefault(popstateUrl)) {
      this.initLoadPage();
      this.usedBrowser = true;
    }
  }

  #shouldCapturePopstate(url) {
    if (!this.popstateCaptureUrls.length || !url) return false;

    const normalizedUrl = url.split('?')[0].split('#')[0];

    for (let i = 0; i < this.popstateCaptureUrls.length; i++) {
      const captureUrl = this.popstateCaptureUrls[i];
      if (typeof captureUrl !== 'string') continue;

      const normalizedCaptureUrl = captureUrl.split('?')[0].split('#')[0];
      if (normalizedCaptureUrl === normalizedUrl) return true;
    }

    return false;
  }

  #handleSubmit(event) {
    const form = event.target.closest('form');
    if (!form) return;

    if (form.classList.contains('js-newsletter-form')) return;

    let action = form.getAttribute('action');
    if (isExternalLink(action)) {
      return;
    }

    if (this.#checkDefault(action)) return;

    event.preventDefault();

    if (form.classList.contains('wpcf7-form') && this.hasLoaded === false) return;

    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }

    this.formData = new FormData(form);

    if (form.getAttribute('method') === 'get') {
      const queryString = new URLSearchParams(this.formData).toString();
      action += '?' + queryString;
      this.formData = undefined;
    } else if (action.indexOf('wpcf7') > -1) {
      action = action.substring(0, action.indexOf('wpcf7')) + '?' + Date.now();
    } else {
      action += '?' + Date.now();
    }

    requestAnimationFrame(() => {
      window.history.pushState(null, this.projectTitle, action);
      this.initLoadPage();
    });
  }

  #handleClick(event) {
    const link = event.target.closest('a');
    if (!link) return;

    let href = link.getAttribute('href');
    if (!href) return;

    if ((link.host === window.location.host
        && href.indexOf('mailto:') === -1
        && href.indexOf('tel:') === -1
        && !this.#isExcludedHref(href)
        && link.getAttribute('target') !== '_blank'
        && !event.ctrlKey
        && !event.metaKey)
      || href.indexOf('?') === 0
    ) {
      if (this.#checkDefault(href)) return;

      event.preventDefault();

      if (typeof link.className !== 'undefined') {
        if (link.className && link.className.indexOf('js-lang-') !== -1) {
          localStorage.removeItem(this.cookiesPrefix + 'SetCookies');
          location.href = href;
          return;
        }
      }

      if (this.isSwitching) {
        window.history.pushState(null, this.projectTitle, href);
        this.initLoadPage();
        return;
      }

      if (this.#checkSamePage(href)) {
        this.samePage(href);
      } else {
        this.pageTypeNext = link.getAttribute('data-pagetype-next');
        this.setPageTypeNext(this.pageTypeNext);

        requestAnimationFrame(() => {
          window.history.pushState(null, this.projectTitle, href);
          this.initLoadPage();
        });
      }
    }
  }

  #isExcludedHref(href) {
    if (!href || !this.excludedHrefParts.length) return false;

    for (let i = 0; i < this.excludedHrefParts.length; i++) {
      if (href.indexOf(this.excludedHrefParts[i]) > -1) return true;
    }

    return false;
  }

  #checkSamePage(url) {
    const cleanHref = url.split('?')[0].split('#')[0];
    const cleanUrl = window.location.href.split('?')[0].split('#')[0];
    const hrefQuery = url.split('?')[1];
    const urlQuery = window.location.href.split('?')[1];

    if ((cleanHref === cleanUrl || cleanHref === '') && hrefQuery === urlQuery) {
      return true;
    } else {
      return false;
    }
  }

  setPageTypeNext = (type) => {
    if (!type) type = '';
    this.pageTypeNext = type;
    document.body.setAttribute('data-pagetype-next', type);
  }

  addExcludedHrefParts = (parts) => {
    if (!Array.isArray(parts)) return;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (typeof part !== 'string') continue;

      const trimmedPart = part.trim();
      if (!trimmedPart) continue;

      if (this.excludedHrefParts.indexOf(trimmedPart) === -1) {
        this.excludedHrefParts.push(trimmedPart);
      }
    }
  }

  setExcludedHrefParts = (parts) => {
    this.excludedHrefParts = [];
    this.addExcludedHrefParts(parts);
  }

  checkWoocommerce = (url) => {
    const currentUrl = url.split('?')[0];

    if (typeof $wcPages !== 'undefined') {
      for (let i = 0; i < $wcPages.length; i++) {
        if (currentUrl.indexOf($wcPages[i]) > -1) return true;
      }
    }

    if (currentUrl.includes('/product/')) return true;

    return false;
  };

  navigateTo = (url) => {
    if (this.#checkDefault(url)) {
      window.location.href = url;
      return;
    }

    if (this.#checkSamePage(url)) {
      this.samePage(url);
      return;
    }

    window.history.pushState(null, this.projectTitle, url);
    this.initLoadPage();
  }

  initLoadPage = () => {
    // console.log('initLoadPage', {url: window.location.href});
    this.usedBrowser = false;

    if (this.isSwitching) {
      this.switchAgain = true;
      return;
    }

    this.url = window.location.href;
    this.isSwitching = true;
    this.canSaveScroll = false;
    this.skipScrollReset = false;

    for (let i = 0; i < this.currentPages.length; i++) {
      if (this.url === this.currentPages[i].url) {
        // console.log('page in dom, skipping load', this.url);
        this.skipScrollReset = true;
        this.beforeSwitch();
        this.#checkSwitch();
        executeScript(this.currentPages[i].script, document.querySelector('#vars'));

        const swapDomFast = () => {
          this.#setHistory(); // sets pagetype, updates cache, sets current page
          this.#endLoading();
        };

        if ('startViewTransition' in document && this.viewTransitions) {
          document.startViewTransition(swapDomFast);
        } else {
          swapDomFast();
        }

        return;
      }
    }

    if (this.loaderEl) this.loaderEl.dataset.status = 'start';
    document.documentElement.classList.add('html--loader');

    this.doneAnimating = false;
    this.doneScrolling = true;
    this.doneLoading = false;
    this.hasSetHtml = false;
    this.hasLoaded = true;

    this.animateBefore(() => this.#setDoneAnimating());

    this.#loadPage(this.url);
  }

  #setHistory() {
    // console.log('setHistory', {$pageType});
    document.body.setAttribute('data-pagetype', $pageType);
    document.body.setAttribute('data-pagetype-next', '');
    this.pageTypeNext = '';

    if (!this.#isCached(window.location.href)) {
      const page = {
        url: window.location.href,
        scroll: 0,
      };

      if (this.useCache) page.html = document.documentElement.innerHTML;

      this.cache.push(page);
      sessionStorage.setItem('historyCache', JSON.stringify(this.cache));
    }

    this.#setCurrent();

    for (let i = 0; i < this.cache.length; i++) {
      if (window.location.href === this.cache[i].url) this.currentCacheIndex = i;
    }
  }

  #checkDefault(url) {
    if (!url) return true;

    let cleanUrl = url.split('?')[0].split('#')[0];
    if (cleanUrl === '') cleanUrl = window.location.href;

    if (
      this.checkWoocommerce(cleanUrl)
      || this.checkWoocommerce(window.location.href)
      || (typeof hasProducts !== 'undefined' && hasProducts)
      || cleanUrl.indexOf('add-to-cart') > -1
      || document.querySelector(`a[href="${url}"][hreflang]`)
    ) {
      return true;
    }

    return false;
  }

  #loadPage(url) {
    if (typeof this.formData === 'object') {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.onload = () => this.#setDoneLoading(xhr, url);
      xhr.send(this.formData);
    } else if (this.useCache && this.#isCached(url)) {
      for (let i = 0; i < this.cache.length; i++) {
        if (url === this.cache[i].url) {
          this.pageElement = document.createElement('div');
          this.pageElement.innerHTML = this.cache[i].html;
          this.#setDoneLoading({ status: 'cache' }, url);
        }
      }
    } else {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url);
      xhr.onload = () => this.#setDoneLoading(xhr, url);
      xhr.send();
    }
  }

  #setDoneLoading(xhr, url) {
    if (this.loaderEl) this.loaderEl.dataset.status = 'loading-done';

    if (xhr.status !== 'cache') {
      this.pageElement = document.createElement('div');
      this.pageElement.innerHTML = xhr.response;
    }

    if (xhr.responseURL){
      const cleanRequestedUrl = url.split('?')[0].split('#')[0];
      const cleanResponseUrl = xhr.responseURL.split('?')[0].split('#')[0];

      if (cleanResponseUrl !== cleanRequestedUrl) {
        // console.warn('Response URL differs from requested URL:', {requestedUrl: url, responseUrl: xhr.responseURL});
        window.history.pushState(null, this.projectTitle, xhr.responseURL);
      }
    }

    this.doneLoading = true;
    this.formData = undefined;

    if (this.doneScrolling && this.doneAnimating) this.#setHtml();
  }

  #setDoneScrolling() {
    if (this.loaderEl) this.loaderEl.dataset.status = 'scrolling-done';
    this.doneScrolling = true;

    if (this.doneLoading && this.doneAnimating) this.#setHtml();
  }

  #setDoneAnimating() {
    if (this.loaderEl) this.loaderEl.dataset.status = 'animation-done';
    this.doneAnimating = true;

    if (this.doneLoading && this.doneScrolling) this.#setHtml();
  }

  #setHtml() {
    // console.log('setHtml', 'old pageType:', $pageType);
    if (this.hasSetHtml) return;
    this.hasSetHtml = true;

    this.beforeSwitch();

    this.pageTypePrev = $pageType;
    this.selectorPrev = $historySelector;

    if (this.#checkSwitch()) return;

    let el = document.createElement('textarea');
    const titleEl = this.pageElement.querySelector('title');
    if (el && titleEl) el.innerHTML = titleEl.innerHTML;
    document.title = el.value;

    const varsScript = this.pageElement.querySelector('#vars script');
    if (varsScript) executeScript(varsScript.innerHTML, document.querySelector('#vars'));

    // console.log('new pageType:', $pageType);

    const swapDom = () => {
      // console.log('swapping DOM');
      el = document.querySelector($historySelector);
      if (el) {
        const sourceEl = this.pageElement.querySelector($historySelector);
        if (sourceEl) el.innerHTML = sourceEl.innerHTML;

        const scriptEls = el.querySelectorAll('script');
        scriptEls.forEach(scriptEl => {
          executeScript(scriptEl.innerHTML, scriptEl);
        });
      }

      // must be here, because startViewTransition is async function
      if ($historySelector === '.items') $historySelector = '.main';

      this.#setHistory(); // sets pagetype, updates cache, sets current page
      this.#endLoading();
    };

    if ('startViewTransition' in document && this.viewTransitions) {
      document.startViewTransition(swapDom);
    } else {
      swapDom();
    }
  }

  #endLoading() {
    // console.log('history end loading');
    this.pageElement = undefined;

    this.newContent(); // external callback. often setPage

    if (this.loaderEl) this.loaderEl.dataset.status = 'new-content';

    const isSameMainSelector = $historySelector === '.main' && this.selectorPrev === '.main';

    if (isSameMainSelector) {
      if (this.usedBrowser) {
        this.setSavedScroll();
      } else if (!this.skipScrollReset) {
        this.setScrollTop(0);
      }
    } else {
      const el = document.querySelector($historySelector);
      if (el) el.scrollTo(0, 0);
    }

    this.canSaveScroll = true;

    if (!this.skipScrollReset) {
      this.#saveScroll();
    }

    this.skipScrollReset = false;
    this.afterSetScroll(); // external callback, often setPageAfterSetScroll, must be called after setting scroll

    setTimeout(() => {
      if (this.loaderEl) this.loaderEl.dataset.status = 'end';
      document.documentElement.classList.remove('html--loader');

      dispatchEvent(new Event('load'));
    }, this.loadEventDelay);

    setTimeout(() => {
      if (this.loaderEl) this.loaderEl.dataset.status = '';
    }, this.resetLineDelay);
  }

  #checkSwitch() {
    this.isSwitching = false;

    if (this.switchAgain) {
      this.switchAgain = false;
      this.initLoadPage();
      return true;
    }

    return false;
  }

  #setCurrent() {
    const varsScript = document.querySelector('#vars script');

    const currentPage = {
      url: this.url,
      script: varsScript ? varsScript.innerHTML : '',
      selector: $historySelector,
    };

    let hasCurrent = false;
    for (let i = 0; i < this.currentPages.length; i++) {
      if ($historySelector === this.currentPages[i].selector) {
        this.currentPages[i] = currentPage;
        hasCurrent = true;
      }
    }

    if (!hasCurrent) this.currentPages.push(currentPage);
  }

  #isCached(url) {
    let inCache = false;
    for (let i = 0; i < this.cache.length; i++) {
      if (url === this.cache[i].url) inCache = true;
    }
    return inCache;
  }

  #saveScroll() {
    const scrollY = window.scrollY || document.documentElement.scrollTop;

    if (!this.isSwitching && this.canSaveScroll && this.cache[this.currentCacheIndex]) {
      this.cache[this.currentCacheIndex].scroll = scrollY;
    }
  }

  setSavedScroll = () => {
    let loadedCacheIndex;
    for (let i = 0; i < this.cache.length; i++) {
      if (window.location.href === this.cache[i].url) loadedCacheIndex = i;
    }

    // console.log('setSavedScroll', {loadedCacheIndex, cache: this.cache}); 
    if (typeof loadedCacheIndex === 'undefined') return;

    const savedScroll = this.cache[loadedCacheIndex].scroll;
    // console.log('setting saved scroll', savedScroll);
    this.setScrollTop(savedScroll);
  }
}
